/**
 * Cross-sell + bundle messaging. Loaded with defer by snippets/product-bundle
 * and (when shop metafield cuddle.bundle_discount is set) by layout/theme.liquid
 * for the cart drawer / cart page.
 *
 * <cross-sell-bundle>  Product page "Frequently bought together". Rows are
 *   server-rendered from the product's cuddle.cross_sell metafield, else loaded
 *   from /recommendations (complementary -> related). Checkboxes, per-item
 *   variant selects, live total, bundle price preview when the configured
 *   automatic discount (data-percent / data-min) would apply, one "Add all to
 *   cart" via /cart/add.js items[] with a success micro-animation.
 *   Dispatches `cart:updated` + `cart:item-added` (drawer, header, toast).
 *
 * Cart offer progress lives in <free-shipping-bar> (assets/theme.js).
 */
(() => {
  const money = (cents) => (window.theme && window.theme.formatMoney ? window.theme.formatMoney(cents) : String(cents));
  const motionOff = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    getComputedStyle(document.documentElement).getPropertyValue('--motion-scale').trim() === '0';
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  /* ---------------------------------------------------------------------- */
  /* <cross-sell-bundle>                                                    */
  /* ---------------------------------------------------------------------- */
  define(
    'cross-sell-bundle',
    class CrossSellBundle extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-list]');
        this.template = this.querySelector('[data-item-template]');
        this.totalEl = this.querySelector('[data-total]');
        this.wasEl = this.querySelector('[data-was]');
        this.unlockedEl = this.querySelector('[data-unlocked]');
        this.hintEl = this.querySelector('[data-hint]');
        this.countEl = this.querySelector('[data-count]');
        this.addButton = this.querySelector('[data-add]');
        this.addLabel = this.querySelector('[data-add-label]');
        this.status = this.querySelector('[data-status]');
        this.main = this.querySelector('[data-main]');
        this.info = this.closest('product-info');
        this.percent = Number(this.dataset.percent) || 0;
        this.min = Number(this.dataset.min) || 2;
        this.cartCount = Number(this.dataset.initialCartCount) || 0;
        if (!this.list || !this.addButton) return;

        this.list.addEventListener('change', (event) => this.onChange(event));
        this.addButton.addEventListener('click', () => this.add());
        if (this.info) {
          this.onVariant = (event) => this.onMainVariant(event.detail && event.detail.variant);
          this.info.addEventListener('product:variant-change', this.onVariant);
        }
        this.onCart = (event) => {
          if (event.detail && typeof event.detail.item_count === 'number') {
            this.cartCount = event.detail.item_count;
            this.update();
          }
        };
        document.addEventListener('cart:updated', this.onCart);

        if (this.dataset.url) this.observeAndLoad();
        else this.update();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.info && this.onVariant) this.info.removeEventListener('product:variant-change', this.onVariant);
        document.removeEventListener('cart:updated', this.onCart);
      }

      observeAndLoad() {
        if (!('IntersectionObserver' in window)) return this.load();
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        // Hidden elements never intersect: observe the parent column.
        this.observer.observe(this.parentElement || this);
      }

      async fetchProducts(url) {
        if (!url) return [];
        try {
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) return [];
          const data = await response.json();
          return Array.isArray(data.products) ? data.products : [];
        } catch (error) {
          return [];
        }
      }

      async load() {
        const ownId = Number(this.dataset.productId);
        const usable = (list) =>
          list.filter((p) => p.id !== ownId && p.available && (p.variants || []).some((v) => v.available)).slice(0, 3);
        let products = usable(await this.fetchProducts(this.dataset.url));
        if (!products.length) products = usable(await this.fetchProducts(this.dataset.fallbackUrl));

        if (!products.length) {
          if (this.hasAttribute('data-design-mode')) {
            this.hidden = false;
            this.querySelector('[data-empty]').hidden = false;
            this.list.hidden = true;
            this.querySelector('.bundle__footer').hidden = true;
          }
          return;
        }

        products.forEach((product, index) => this.list.append(this.renderItem(product, index + 1)));
        this.hidden = false;
        this.update();
      }

      renderItem(product, index) {
        const fragment = this.template.content.cloneNode(true);
        const item = fragment.querySelector('li');
        const variants = product.variants.filter((v) => v.available);
        const first = variants[0];
        item.classList.add('is-new');
        item.style.animationDelay = `${index * 80}ms`;
        item.dataset.variantId = first.id;
        item.dataset.price = first.price;

        item.querySelectorAll('[data-link]').forEach((link) => (link.href = product.url));
        item.querySelector('[data-title]').textContent = product.title;
        const newPill = item.querySelector('[data-new]');
        const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
        if (newPill && tags.some((tag) => String(tag).trim().toLowerCase() === 'new')) newPill.hidden = false;
        item.querySelector('[data-check-label]').textContent = (this.dataset.textAddItem || '').replace('[title]', product.title);

        const img = item.querySelector('[data-image]');
        const src = product.featured_image && typeof product.featured_image === 'object' ? product.featured_image.url : product.featured_image;
        if (src) {
          const url = new URL(src, window.location.href);
          url.searchParams.set('width', '192');
          img.src = url.toString();
        } else {
          img.remove();
        }

        const select = item.querySelector('[data-select]');
        if (variants.length > 1) {
          select.hidden = false;
          select.setAttribute('aria-label', (this.dataset.textOptions || '').replace('[title]', product.title));
          variants.forEach((variant) => {
            const option = document.createElement('option');
            option.value = variant.id;
            option.dataset.price = variant.price;
            option.textContent = variant.title;
            select.append(option);
          });
        } else {
          select.remove();
        }
        item.querySelector('[data-price]').textContent = money(first.price);
        return item;
      }

      onChange(event) {
        const select = event.target.closest('[data-select]');
        if (select) {
          const item = select.closest('.bundle__item');
          const chosen = select.selectedOptions[0];
          if (item && chosen) {
            item.dataset.variantId = chosen.value;
            item.dataset.price = chosen.dataset.price;
            const price = item.querySelector('[data-price]');
            if (price) price.textContent = money(chosen.dataset.price);
          }
        }
        const check = event.target.closest('[data-check]');
        if (check && check.checked && !motionOff()) {
          const box = check.nextElementSibling;
          if (box) box.animate([{ transform: 'scale(0.8)' }, { transform: 'scale(1.15)' }, { transform: 'scale(1.05)' }], { duration: 320, easing: 'cubic-bezier(0.34, 1.36, 0.64, 1)' });
        }
        this.update();
      }

      onMainVariant(variant) {
        if (!this.main) return;
        const check = this.main.querySelector('[data-check]');
        const available = Boolean(variant && variant.available);
        if (variant) {
          this.main.dataset.variantId = variant.id;
          this.main.dataset.price = variant.price;
          const price = this.main.querySelector('[data-main-price]');
          if (price) price.textContent = variant.priceHtml ? variant.priceHtml : money(variant.price);
          const title = this.main.querySelector('[data-main-variant]');
          if (title) title.textContent = variant.title;
        }
        check.disabled = !available;
        if (!available) check.checked = false;
        this.update();
      }

      items() {
        return Array.from(this.list.querySelectorAll(':scope > .bundle__item'));
      }

      selectedItems() {
        return this.items().filter((item) => {
          const check = item.querySelector('[data-check]');
          return check && check.checked && !check.disabled;
        });
      }

      update() {
        const items = this.items();
        const selected = this.selectedItems();
        items.forEach((item) => {
          const check = item.querySelector('[data-check]');
          item.classList.toggle('is-off', !(check && check.checked));
        });
        const total = selected.reduce((sum, item) => sum + Number(item.dataset.price || 0), 0);

        // Bundle preview only mirrors the configured automatic discount.
        const unlocked = this.percent > 0 && selected.length > 0 && selected.length + this.cartCount >= this.min;
        const discounted = Math.round((total * (100 - this.percent)) / 100);
        this.totalEl.textContent = money(unlocked ? discounted : total);
        if (this.wasEl) {
          this.wasEl.hidden = !unlocked;
          this.wasEl.textContent = unlocked ? money(total) : '';
        }
        if (this.unlockedEl) this.unlockedEl.hidden = !unlocked;
        if (this.hintEl) this.hintEl.hidden = unlocked;
        this.classList.toggle('is-unlocked', unlocked);

        const enabled = items.filter((item) => {
          const check = item.querySelector('[data-check]');
          return check && !check.disabled;
        });
        const all = selected.length > 1 && selected.length === enabled.length;
        if (this.addLabel) this.addLabel.textContent = all ? this.dataset.textAddAll : this.dataset.textAddSelected;
        this.countEl.textContent = selected.length ? `(${selected.length})` : '';
        this.addButton.disabled = selected.length === 0;
      }

      async add() {
        const selected = this.selectedItems();
        if (!selected.length || this.addButton.getAttribute('aria-busy') === 'true') return;
        clearTimeout(this.resetTimer);
        delete this.addButton.dataset.state;
        this.addButton.setAttribute('aria-busy', 'true');
        this.status.textContent = '';

        const items = selected.map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 }));
        try {
          // Shared cart API (theme.js): add.js + drawer section + fresh cart.js.
          const { item, cart, sections } = await window.theme.cartAdd({ items });
          this.addButton.removeAttribute('aria-busy');
          this.addButton.dataset.state = 'added';
          this.status.textContent = this.dataset.textAdded;
          this.celebrate();
          window.theme.announceAdd({ item, cart, sections, source: this.addButton });
          this.resetTimer = setTimeout(() => delete this.addButton.dataset.state, 2400);
        } catch (error) {
          this.addButton.removeAttribute('aria-busy');
          this.addButton.dataset.state = 'error';
          this.status.textContent = error.message || this.dataset.textError;
          this.resetTimer = setTimeout(() => delete this.addButton.dataset.state, 2600);
        }
      }

      celebrate() {
        if (motionOff()) return;
        this.classList.remove('is-added');
        void this.offsetWidth;
        this.classList.add('is-added');
        setTimeout(() => this.classList.remove('is-added'), 900);
      }
    }
  );
})();
